<template>
  <div
    :id="file.id"
    class="custom-file"
    :class="[
      `custom-file_${fileMarkup}`,
      {
        'is-deleted': isRemoved,
        'is-previewable': isPreviewable,
        'is-not-openable': isNotOpenable
      }
    ]"
  >
    <div
      class="custom-file__in"
      @click="showImage()"
    >
      <template v-if="isElType">
        <FilePic
          :isRemoved="isRemoved"
          :fileName="file.name"
        />
        <span class="custom-file__desc">
          <button
            v-tooltip="{
              content: file.name,
              theme: 'info-tooltip',
              placement: 'bottom',
              popperClass: 'custom-file__tooltip'
            }"
            type="button"
            class="custom-file__name"
            @click.prevent.stop="showImage()"
          >
            {{ file.name }}
          </button>
          <template v-if="!isRemoved">
            <span
              class="custom-file__meta"
              :class="{ 'is-error': isOverSizeLimit }"
            >
              {{ downloadStatus ? $t(`fileDownloadStatus.${downloadStatus}`) : fileMaxSize }}
            </span>
            <ProgressLoadingLine
              class="custom-file__progress"
              :class="[{ 'is-hidden': (progress === 0 && !isUploading) || isOverSizeLimit }]"
              :progress="progress"
            />
            <button
              v-if="removeButton"
              type="button"
              class="custom-file__remove"
              @click.stop="$emit('remove', file)"
            >
              <icon
                data="@icon/x-circle.svg"
                :fill="false"
                color="currentColor"
                width="var(--icon-16)"
                height="var(--icon-16)"
              />
            </button>
          </template>
        </span>
        <button
          v-if="!isRemoved && !removeButton"
          type="button"
          class="custom-file__download"
          :class="{
            'is-downloading': downloadStatus,
            'is-preparing': downloadStatus === 'preparing'
          }"
          @click.prevent.stop="onFileDownload"
        >
          <Spinner
            v-if="downloadStatus"
            :loading="true"
            className="custom-file__download-spinner"
          >
            <icon
              v-if="downloadStatus === 'preparing'"
              data="@icon/spinner.svg"
              width="var(--icon-24)"
              height="var(--icon-24)"
              color="var(--monochrome02to36)"
              class="spin-animation"
            />
            <icon
              v-else-if="isDark"
              data="@icon/spinner-gradient-dark.svg"
              original
              width="var(--icon-24)"
              height="var(--icon-24)"
              class="spin-animation"
            />
            <icon
              v-else
              data="@icon/spinner-gradient.svg"
              original
              width="var(--icon-24)"
              height="var(--icon-24)"
              class="spin-animation"
            />
          </Spinner>
          <FileDownloadPic v-else />
        </button>
        <BaseSubHeading
          v-if="isRemoved"
          weight="500"
          size="extra-small"
          className="custom-file__deleted-info"
          color="monochrome-05"
          darkColor="monochrome-04"
        >
          Deleted by {{ file.deletedBy?.fullName }}
        </BaseSubHeading>
      </template>

      <template v-if="isLineType">
        <button
          type="button"
          @click.prevent.stop="showImage()"
        >
          <FilePic
            :isRemoved="isRemoved"
            :fileName="file.name"
          />
        </button>
        <template v-if="!isRemoved">
          <div class="custom-file__content">
            <div class="custom-file__row">
              <button
                type="button"
                class="custom-file__row-item custom-file__row-left"
                @click.prevent.stop="showImage()"
              >
                <BaseSubHeading
                  v-tooltip="{
                    content: file.name,
                    theme: 'info-tooltip',
                    placement: 'bottom',
                    popperClass: 'custom-file__tooltip'
                  }"
                  weight="400"
                  size="medium"
                  className="custom-file__cut-name"
                  color="monochrome-07"
                >
                  {{ file.name }}
                </BaseSubHeading>

                <BaseSubHeading
                  weight="500"
                  size="extra-small"
                  className="custom-file__size"
                  color="monochrome-05"
                  darkColor="monochrome-04"
                >
                  {{ downloadStatus ? $t(`fileDownloadStatus.${downloadStatus}`) : fileSize }}
                </BaseSubHeading>
              </button>
              <div class="custom-file__row-item custom-file__row-right">
                <button
                  type="button"
                  class="custom-file__download"
                  :class="{
                    'is-downloading': downloadStatus,
                    'is-preparing': downloadStatus === 'preparing'
                  }"
                  @click.prevent.stop="onFileDownload"
                >
                  <Spinner
                    v-if="downloadStatus"
                    :loading="true"
                    className="custom-file__download-spinner"
                  >
                    <icon
                      v-if="downloadStatus === 'preparing'"
                      data="@icon/spinner.svg"
                      width="var(--icon-24)"
                      height="var(--icon-24)"
                      color="var(--monochrome02to36)"
                      class="spin-animation"
                    />
                    <icon
                      v-else-if="isDark"
                      data="@icon/spinner-gradient-dark.svg"
                      original
                      width="var(--icon-24)"
                      height="var(--icon-24)"
                      class="spin-animation"
                    />
                    <icon
                      v-else
                      data="@icon/spinner-gradient.svg"
                      original
                      width="var(--icon-24)"
                      height="var(--icon-24)"
                      class="spin-animation"
                    />
                  </Spinner>
                  <FileDownloadPic v-else />
                </button>

                <span
                  v-tooltip.bottom="{ content: fileCreateDate, theme: 'info-tooltip' }"
                  class="desc_label desc_normal custom-file_time-ago"
                >
                  <BaseSubHeading
                    weight="400"
                    size="large"
                    color="monochrome-05"
                    darkColor="monochrome-04"
                  >
                    {{ timeAgo }}
                  </BaseSubHeading>
                </span>
              </div>
            </div>
            <div class="custom-file__sub-row">
              <ProgressLoadingLine
                class="custom-file__progress"
                :class="[{ 'is-hidden': (progress === 0 && !isUploading) || isOverSizeLimit }]"
                :progress="progress"
              />
            </div>
          </div>

          <Avatar
            :src="fileOwner.pictureUrl"
            :gap="fileOwner.getInitials()"
          />

          <DropdownTooltip
            :skidding="8"
            iconSize="md"
            placement="bottom-end"
            class="custom-file__menu"
            :class="{ 'is-hidden': hideMenu }"
            :disabled="hideMenu"
            @click.stop
          >
            <template #tooltip-content="{ hide }">
              <ul>
                <li
                  v-for="(option, index) in moreOptions"
                  :key="index"
                >
                  <button
                    :id="option.id"
                    class="dropdownTooltip_remove dropdownTooltip"
                    @click.prevent="deleteFile(hide)"
                  >
                    <icon
                      data="@icon/trash.svg"
                      width="1.5em"
                      height="1.5em"
                      :fill="true"
                      color="#7D92A7"
                    />
                    {{ option.label }}
                  </button>
                </li>
              </ul>
            </template>
          </DropdownTooltip>
        </template>
        <template v-else>
          <div class="custom-file__content">
            <BaseSubHeading
              v-tooltip="{
                content: file.name,
                theme: 'info-tooltip',
                placement: 'bottom',
                popperClass: 'custom-file__tooltip'
              }"
              weight="400"
              size="medium"
              className="custom-file__cut-name"
              color="monochrome-07"
            >
              {{ file.name }}
            </BaseSubHeading>
          </div>
          <BaseSubHeading
            weight="500"
            size="small"
            className="custom-file__deleted-info"
            color="monochrome-05"
            darkColor="monochrome-04"
          >
            Deleted by {{ file.deletedBy?.fullName }}
          </BaseSubHeading>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"

import Avatar from "@/components/Avatar/index.vue"
import DropdownTooltip from "@/components/Dropdown/DropdownTooltip.vue"
import FileDownloadPic from "@/components/File/FileDownloadPic.vue"
import FilePic from "@/components/File/FilePic.vue"
import ProgressLoadingLine from "@/components/ProgressBar/ProgressLoadingLine.vue"
import Spinner from "@/components/Spinner/index.vue"

import {
  checkNotOpenable,
  getBrowserPreviewMimeType,
  isPdfConversionPreviewable,
  PDF_PREVIEW_MIME_TYPE
} from "@/helpers/fileType"
import formatBytes from "@/helpers/formatBytes"
import { FileDownloadSource, FileDownloadStatusesBySource, FileMetaData } from "@/models"
import { Actions, Getters } from "@/store"
import { formatDate, formatDateAgo } from "@/utils/dateUtil"
import { openFilePreview } from "@/utils/openFilePreview"

@Options({
  name: "FileItem",
  components: {
    FileDownloadPic,
    FilePic,
    Avatar,
    DropdownTooltip,
    ProgressLoadingLine,
    Spinner
  }
})
export default class FileItem extends Vue {
  @Prop({
    required: true
  })
  file: FileMetaData
  @Prop({
    default: []
  })
  files: FileMetaData[]
  @Prop({
    default: false
  })
  removeButton: boolean
  @Prop({
    default: 0
  })
  progress: number
  /** Shows the progress bar from 0%, so a file still waiting in the upload queue doesn't look uploaded. */
  @Prop({ default: false }) isUploading: boolean
  @Prop({
    default: false
  })
  preventShow: boolean
  @Prop({
    default: false
  })
  hideMenu: boolean
  @Prop({
    default: ""
  })
  fancyboxGroup: string
  @Prop({ default: true }) projectScoped: boolean
  @Prop({
    default: "element",
    validator: (type: string) => ["element", "line"].includes(type)
  })
  fileMarkup: "element" | "line"

  @Getter(Getters.GET_CURRENT_PROJECT_ID) projectId: string
  @Getter(Getters.THEME_IS_DARK) readonly isDark: boolean
  @Getter(Getters.GET_MAX_UPLOAD_SIZE_KB) maxUploadSizeKb: number
  @Getter(Getters.GET_MAX_UPLOAD_SIZE_LABEL) maxUploadSizeLabel: string
  @Getter(Getters.GET_DOWNLOAD_STATUSES) downloadStatuses: Record<
    string,
    FileDownloadStatusesBySource
  >

  @Action(Actions.ATTACHMENT_DOWNLOAD) downloadFile: ({
    fileMetadataId,
    fileName,
    source
  }: {
    fileMetadataId: string
    fileName: string
    source: FileDownloadSource
  }) => void
  @Action(Actions.ATTACHMENT_VIEW) attachmentView: (payload: {
    fileMetadataId: string
    fullSize?: boolean
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_DOCUMENT_PREVIEW) attachmentDocumentPreview: (payload: {
    fileMetadataId: string
  }) => Promise<Blob>
  @Action(Actions.ATTACHMENT_VIDEO_URL) videoUrl: ({
    projectId,
    attachmentId,
    projectScoped
  }: {
    projectId: string
    attachmentId: string
    projectScoped?: boolean
  }) => Promise<string>

  moreOptions = [
    {
      id: "delete",
      label: "Delete",
      icon: require("@icon/trash.svg"),
      iconOption: {
        width: "1.5em",
        height: "1.5em"
      }
    }
  ]

  get isRemoved() {
    return this.file.deleted
  }

  get fileMaxSize() {
    return this.getFileSize(this.file.size)
  }

  get fileSize() {
    return this.getFileSize(this.file.size)
  }

  get fileOwner() {
    return this.file.owner
  }

  get downloadStatus() {
    return this.downloadStatuses[this.file.id]?.[this.fileMarkup]
  }

  get isLineType() {
    return this.fileMarkup === "line"
  }

  get isElType() {
    return this.fileMarkup === "element"
  }

  get timeAgo() {
    return formatDateAgo({
      date: new Date(),
      dateFormat: this.file.createdDateParsed,
      short: true
    })
  }

  get fileCreateDate() {
    return formatDate(this.file.createdDateParsed, "MM/dd/yy h:mm bb")
  }

  get isOverSizeLimit() {
    return this.file.size / 1024 > this.maxUploadSizeKb
  }

  get filteredFiles() {
    const mediaFiles = this.files.filter(file => (file.isImage || file.isVideo) && !file.deleted)

    /** Historical attachments may not be in the loaded comment collection, but must open themselves. */
    if (
      (this.file.isImage || this.file.isVideo) &&
      !this.isRemoved &&
      !mediaFiles.some(file => file.id === this.file.id)
    ) {
      return [this.file, ...mediaFiles]
    }

    return mediaFiles
  }

  get isBrowserPreviewable() {
    return !this.file.custom && Boolean(getBrowserPreviewMimeType(this.file.name))
  }

  get isPdfConversionPreviewable() {
    return !this.file.custom && isPdfConversionPreviewable(this.file.name)
  }

  get isPreviewable() {
    return (
      !this.preventShow &&
      !this.isRemoved &&
      (this.isBrowserPreviewable ||
        this.isPdfConversionPreviewable ||
        this.file.isImage ||
        this.file.isVideo)
    )
  }

  get isNotOpenable() {
    return checkNotOpenable(this.file.name)
  }

  get jumpToIndex() {
    const index = this.filteredFiles.findIndex(file => file.id === this.file.id)

    return index < 0 ? 0 : index
  }

  getFileSize(size) {
    return size === 0
      ? "0 Bytes"
      : this.isOverSizeLimit
        ? `Maximum size for file is ${this.maxUploadSizeLabel}`
        : this.formatBytes(size.toString())
  }
  formatBytes(value: string) {
    return formatBytes(value)
  }
  onFileDownload() {
    if (this.isRemoved) return
    const { id, name }: { id: string; name: string } = this.file
    if (this.file.custom) return

    this.downloadFile({
      fileMetadataId: id,
      fileName: name,
      source: this.fileMarkup
    })
  }

  @Emit("deleteFile")
  deleteFile(hide) {
    hide()
    return this.file
  }

  // Видалено непотрібну логіку з тултіпами

  async showImage() {
    if (this.preventShow || this.isRemoved) return
    if (this.isPdfConversionPreviewable) {
      await this.openConvertedDocumentPreview()
      return
    }
    if (this.isBrowserPreviewable) {
      await this.openBrowserPreview()
      return
    }
    if (!this.file.isImage && !this.file.isVideo) return

    if (!this.fancyboxGroup.length) {
      const item = {
        fileMetadataId: this.file.id,
        type: this.file.isImage ? "image" : "html5video",
        previewBlob: this.file.isImage ? this.attachmentView : null,
        loadFullSize: this.file.isImage,
        src: this.file.isVideo
          ? await this.videoUrl({
              projectId: this.projectId,
              attachmentId: this.file.id,
              projectScoped: this.projectScoped
            })
          : "",
        caption: this.file.name
      }

      this.$fancybox.show([item])
      return
    }

    const galleryItems = await Promise.all(
      this.filteredFiles.map(async file => ({
        type: file.isImage ? "image" : "html5video",
        previewBlob: file.isImage ? this.attachmentView : null,
        loadFullSize: file.isImage,
        src: file.isVideo
          ? await this.videoUrl({
              projectId: this.projectId,
              attachmentId: file.id,
              projectScoped: this.projectScoped
            })
          : "",
        fileMetadataId: file.id,
        caption: file.name
      }))
    )

    if (!galleryItems.length) return

    this.$fancybox.show(galleryItems, { startIndex: this.jumpToIndex })
  }

  async openBrowserPreview() {
    if (this.file.custom) return

    await openFilePreview(this.file.name, () =>
      this.attachmentView({ fileMetadataId: this.file.id, fullSize: true })
    )
  }

  async openConvertedDocumentPreview() {
    if (this.file.custom) return

    await openFilePreview(
      this.file.name,
      () => this.attachmentDocumentPreview({ fileMetadataId: this.file.id }),
      PDF_PREVIEW_MIME_TYPE
    )
  }

  beforeUnmount() {
    this.$fancybox.unbind()
  }
}
</script>
